import type { Dispatch } from "redux";

import { showError } from "@/utils/notification";

import { useRecordingDock } from "@components/RecordingDock/store/useRecordingDock";

/**
 * Arbitration for the single global `activeTranscriberId` slot.
 *
 * Only one Transcriber may hold the mic at a time, so a Transcriber that is
 * transcribing while some OTHER id owns the slot normally stands down. That rule
 * is correct for dictation mics (two of them must never run at once) but it was
 * also being applied to the persistent assessment-recording session, which is
 * mounted app-wide by `RecordingHost`. Starting dictation anywhere — e.g. Report
 * Builder, the caret mic — claimed the slot and silently stopped a live
 * assessment recording (MDRS-233).
 *
 * The persistent session is therefore exempt: it never yields the mic to a
 * dictation transcriber that claims the slot after it.
 */
export type YieldDecisionArgs = {
  /** Whether this Transcriber currently has a live session. */
  isTranscribing: boolean;
  /** Whether this Transcriber owns the global active slot. */
  isActiveTranscriber: boolean;
  /**
   * True for the app-wide recording host's Transcriber. Exempt from eviction —
   * an assessment recording outranks any dictation mic.
   */
  isPersistentSession: boolean;
};

export const shouldYieldToActiveTranscriber = ({
  isTranscribing,
  isActiveTranscriber,
  isPersistentSession,
}: YieldDecisionArgs): boolean => {
  if (!isTranscribing) return false;
  if (isPersistentSession) return false;
  return !isActiveTranscriber;
};

/**
 * Whether the persistent assessment recording is currently capturing.
 *
 * Read imperatively (not via a hook) so the check happens at click time and
 * doesn't subscribe every Transcriber in the tree to store churn.
 */
export const isRecordingSessionLive = (): boolean => {
  const { status } = useRecordingDock.getState();
  return status === "connecting" || status === "recording" || status === "paused";
};

/** Explains why a dictation mic was refused while a recording is in progress. */
export const showRecordingConflictMessage = (dispatch: Dispatch): void => {
  showError(
    "Can't start dictation while an assessment recording is in progress. Finish the recording first.",
    dispatch
  );
};
